import React, { useState, useEffect, useRef } from 'react'
import PropTypes, { oneOfType } from 'prop-types'
import AntModal from '../Modal'
import { IconButton, Stack, TextField, Typography, Button, styled } from '@mui/material'
import SearchLanguages from 'myComponents/UseFullAutoCompletes/SearchLanguages'
import { useBeforeunload } from 'react-beforeunload'
import MinimizeIcon from '@mui/icons-material/Minimize';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useSelector, useDispatch } from 'react-redux'
import { closeExistedAudioSubtitleModal, closeUploadAudioAndSubModal, modifyExistedLanguages, uploadSubAndAudioCompleted } from 'Redux/UploadAudioAndSubtitle/actions'
import MakeSureClose from './MakeSureClose.modal'
import { editAudioAndSubtitle, removeAudioAndSubtitle, uploadSubtitle_API } from 'api/Video/videoApi'
import { toastSuccess, toastWarning } from 'components/Toasts/Toasts'
import RemoveModal from '../RemoveModal'


const Input = styled('input')({
    display: 'none',
});

const UploadSubtitleAndDubbed = () => {
    // in this modal :
    // User can choose the language and Enter the Title and Upload File
    // in Add Mode : User can Upload File
    // in Edit Mode : user Only can : {
    //  1. change the language
    //  2. change title
    //  3. Remove the File
    //  4. Notice that the Upload Button Will Not Displayed in This Mode ( Edit Mode ) 
    //}
    /* ****************************************************** Redux ***************************************************** */
    const { isAudio, sourceId, onDataAddedSuccessfully, selectedLanguage, fileId } = useSelector(state => state.uploadAudioAndSubtitleModal);
    const dispatch = useDispatch();
    const modalTitle = isAudio ? 'Audio (Dub)' : 'Subtitle';
    const inputRef = useRef();

    /* ***************************************************** States ***************************************************** */
    const [dataForm, setDataForm] = useState({
        selectedLanguage: null,
        title: '',
    });
    const [modalWindow, setModalWindow] = useState('NORMAL');
    const [makeSureCloseModal, setMakeSureCloseModal] = useState(false);
    const [uploadStarted, setUploadStarted] = useState(false);
    const [removeModalVisiblity, setRemoveModalVisiblity] = useState(null)
    const [removeLoading, setRemoveLoading] = useState(false)
    const [updateLoading, setUpdateLoading] = useState(false)
    const [file, setFile] = useState(null);

    /* **************************************************** Functions *************************************************** */

    const handleUpdateFileData = () => {

        if (selectedLanguage) { // Edit mode
            setUpdateLoading(true)
            const data = {
                isAudio,
                languageId: selectedLanguage.language.id,
                title: dataForm.title
            }

            editAudioAndSubtitle(fileId, data)
                .then(() => {
                    toastSuccess('File Updated !')
                    setUpdateLoading(false)
                    onDataAddedSuccessfully()
                    dispatch(closeExistedAudioSubtitleModal())
                    dispatch(closeUploadAudioAndSubModal())
                })
                .catch(() => {
                    setUpdateLoading(false)
                })
        } else {
            if (canUpload()) {
                setUpdateLoading(true)
                uploadSubtitle_API(sourceId, file, dataForm.title, dataForm.selectedLanguage.id)
                    .then(() => {
                        toastSuccess('File Uploaded !')
                        setUpdateLoading(false)
                        onDataAddedSuccessfully()
                        dispatch(closeExistedAudioSubtitleModal())
                        dispatch(closeUploadAudioAndSubModal())
                    })
                    .catch(() => {
                        setUpdateLoading(false)
                    })
            }
        }
    }

    const canUpload = () => {
        if (!selectedLanguage) {
            return dataForm.selectedLanguage && file && dataForm.title
        }
    }

    const removeFile = () => {
        setRemoveLoading(true)
        removeAudioAndSubtitle(fileId)
            .then(() => {
                setRemoveLoading(false)
                toastWarning('file Removed !')
                onDataAddedSuccessfully()
                dispatch(modifyExistedLanguages({
                    itemForModify: selectedLanguage,
                    modifyType: 'remove'
                }))
                dispatch(closeUploadAudioAndSubModal())
            })
            .catch(() => {
                setRemoveLoading(false)
            })
    }

    const handleRemoveState = (value) => {
        setRemoveModalVisiblity(value)
    }

    const closeModal = () => {
        dispatch(closeUploadAudioAndSubModal())
    };

    const closeModalWhenUploadCompleted = () => {
        dispatch(uploadSubAndAudioCompleted())
    }

    const handleChange = (name, data) => {
        setDataForm(prev => ({ ...prev, [name]: data }))
    };

    const onInputFileChanged = (file) => {
        setFile(file[0])
    };

    const minimize = () => {
        setModalWindow('MINIMIZE');
        dispatch(closeExistedAudioSubtitleModal())
    };

    const maximize = () => {
        setModalWindow('NORMAL');
    };

    const confirmationModal = () => {
        setMakeSureCloseModal(prev => !prev);
    };

    useBeforeunload((event) => {
        if (uploadStarted) {
            event.preventDefault()
        }
    })

    /* *************************************************** sideEffects ************************************************** */
    useEffect(() => {
        if (selectedLanguage) {
            const { title, language } = selectedLanguage;
            setDataForm((prev) => ({
                ...prev,
                title,
                selectedLanguage: language
            }))
        }
    }, [])

    return (
        <AntModal
            uploadModal
            visible={Boolean(sourceId)}
            fullWidth={modalWindow === 'NORMAL'}
            maxWidth='sm'
            onOkText='Update'
            onOk={handleUpdateFileData}
            okButtonProps={{
                sx: { display: 'inline-block', cursor: canUpload() ? "" : "not-allowed" },
                variant: 'contained'
            }}
            loading={updateLoading}

            // on Cancel the Modal
            onCancelText={uploadStarted ? 'Cancel Upload' : 'Cancel'}
            onCancel={uploadStarted ? confirmationModal : closeModal}
            cancelButtonProps={{ color: uploadStarted ? 'error' : 'primary' }}

            // on User Select Remove on Edit Mode
            removeText='Remove File'
            onClickRemove={() => handleRemoveState(true)}
            removeButton={!!selectedLanguage}

            onBackdropClick={uploadStarted ? confirmationModal : closeModal}
            hideBackDrop={modalWindow === 'MINIMIZE'}
            isDraggable={modalWindow === 'MINIMIZE'}
            noAction={modalWindow === 'MINIMIZE'}
            titleFlexDirection="column"
            title={
                <Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography fontWeight="bold" sx={{ marginRight: 2, flexDirection: 'column' }}>
                            {!selectedLanguage ?
                                uploadStarted ? `Uploading ${modalTitle} . . .` : `Upload Video ${modalTitle}`
                                : `Edit ${modalTitle}`
                            }
                        </Typography>
                        <Stack>
                            {modalWindow === 'NORMAL' ? (
                                uploadStarted ? (
                                    <IconButton onClick={minimize} size="small" edge="start">
                                        <MinimizeIcon />
                                    </IconButton>
                                ) : (
                                    <IconButton onClick={uploadStarted ? confirmationModal : closeModal} size="small" LinkComponent='hello'>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )
                            ) : (
                                <Stack direction="row" justifyContent="space-evenly">
                                    <IconButton onClick={maximize} size="small">
                                        <OpenInFullIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton onClick={uploadStarted ? confirmationModal : closeModal} size="small">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            }
        >
            {/*Content of Upload Modal */}
            <Stack justifyContent='center' direction='row'>
                <Stack width={400}>
                    {
                        modalWindow !== 'MINIMIZE' &&
                        <React.Fragment>
                            <SearchLanguages
                                defaultValue={dataForm.selectedLanguage}
                                value={dataForm.selectedLanguage}
                                onChange={(e, data) => handleChange('selectedLanguage', data)}
                                label='Language of File'
                                disabled={uploadStarted}
                                required
                            />

                            <TextField
                                defaultValue={dataForm.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                label='Title'
                                placeholder={`Enter a Title for this ${modalTitle}`}
                                disabled={uploadStarted}
                                sx={{ mt: 1 }}

                            />
                        </React.Fragment>
                    }
                    {
                        !selectedLanguage && (
                            <Stack sx={{ my: uploadStarted ? 1 : 0 }}>
                                {!uploadStarted && (
                                    <label htmlFor="my-file-input">
                                        <Input
                                            ref={inputRef}
                                            accept={"*.srt"}
                                            id="my-file-input"
                                            multiple={false}
                                            type="file"
                                            onChange={e => onInputFileChanged(e.target.files)}
                                        />
                                        <Button
                                            variant="contained"
                                            color={file === null ? "primary" : 'success'}
                                            component="span"
                                            fullWidth={true}
                                            sx={{ mt: 2 }}
                                        >
                                            {
                                                file === null
                                                    ? "Choose File"
                                                    : file.name
                                            }
                                        </Button>
                                    </label>
                                )}
                            </Stack>
                        )
                    }
                </Stack>
            </Stack>

            {
                (uploadStarted && makeSureCloseModal) && (
                    <MakeSureClose
                        onOk={closeModal}
                        onCancel={confirmationModal}
                    />
                )
            }
            {
                removeModalVisiblity && (
                    <RemoveModal
                        loading={removeLoading}
                        onClose={() => handleRemoveState(false)}
                        onOk={removeFile}
                    />
                )
            }
        </AntModal>
    )
}

UploadSubtitleAndDubbed.propTypes = {
    onCancel: PropTypes.func,
    isAudio: PropTypes.bool,
    sourceId: oneOfType([PropTypes.string, PropTypes.number])
}

export default UploadSubtitleAndDubbed